/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore, AuthUser } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { peekResumableRun } from './engine/net/run-resume';
import { EntryVeil } from './engine/ui/entry-veil';
import { mountBackToLanding } from './engine/ui/back-to-landing';
import { keepAltchaFresh } from './engine/ui/altcha-freshness';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

/**
 * Code splitting (perf pass 2026-10-18): the game (three.js, Colyseus, every
 * scene and window) and the admin panel are separate chunks, so /login and the
 * other auth screens download ~1/5 of the JS they used to. The dungeon module
 * is kept once loaded because the unload handler below must call into it
 * synchronously.
 */
type DungeonModule = typeof import('./engine/dungeon-instance-bootstrap');
let dungeonModule: DungeonModule | null = null;
const loadDungeon = (): Promise<DungeonModule> => import('./engine/dungeon-instance-bootstrap').then((m) => (dungeonModule = m));
const loadSession = () => Promise.all([import('./engine/session-bootstrap'), loadDungeon()]).then(([m]) => m);

/** Mounts a lazily loaded screen; the returned teardown also cancels a mount still in flight. */
function lazyMount(root: HTMLElement, load: () => Promise<() => void>): () => void {
  let dispose: (() => void) | null = null;
  let gone = false;
  realMounts += 1;
  root.innerHTML = '<div class="grid place-items-center h-screen bg-lobby text-sm text-text-dim">Loading\u2026</div>';
  load().finally(() => { realMounts -= 1; }).then((d) => { if (gone) d(); else dispose = d; }).catch((err) => {
    console.error('[boot] failed to load a screen', err);
    if (gone) return;
    root.innerHTML = `<div class="grid place-items-center h-screen bg-lobby text-sm text-text-dim"><div class="grid gap-3 justify-items-center text-center px-4">
      <span>Could not load the game. Check your connection and try again.</span>
      <button type="button" data-reload class="btn btn-stone min-h-11 min-w-11 px-5">Reload</button></div></div>`;
    root.querySelector('[data-reload]')?.addEventListener('click', () => location.reload());
  });
  return () => { gone = true; dispose?.(); dispose = null; };
}

/**
 * A tab left open across a deploy asks for chunk names that no longer exist
 * (server.js answers 404). Reload once to pick up the new build; the
 * sessionStorage stamp stops a loop if the chunk is genuinely unreachable —
 * then `lazyMount` shows its Reload button instead.
 */
/** Screens currently loading for real (not the login-page prefetch). */
let realMounts = 0;

window.addEventListener('vite:preloadError', (event) => {
  // A failed background prefetch on /login must never reload: the player would lose what they typed.
  // The error propagates to the prefetch's own catch, and the real mount retries the import.
  if (realMounts === 0) return;
  const KEY = 'hullrot.chunkReloadAt';
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY) ?? 0); } catch { /* private mode */ }
  if (Date.now() - last < 60_000) return;
  try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
  event.preventDefault();
  location.reload();
});

/** Warm the game chunk while the player is still on an auth screen, so /play opens without a download wait. */
function prefetchGame(): void {
  const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  const go = () => { void loadSession().catch(() => { /* retried on mount */ }); };
  if (idle) idle(go, { timeout: 4000 }); else setTimeout(go, 1500);
}

/**
 * `/play` mounts the Sconce Hub by default (design/gdd/sconce-hub.md) — the
 * real production path into a match is queueing from there, not a direct
 * join. `?view=dungeon` mounts the procedural-generation layout viewer.
 * `?view=dungeon_instance` is a dev/QA bypass straight into a match,
 * skipping Matchmaking entirely — kept for the same direct-join testing
 * this route always supported, now that the bare route goes through the hub.
 */
function mountByView(view: string | null, root: HTMLElement, user: AuthUser): () => void {
  const onLogout = () => { logout(); disconnectSocket(); navigate('/login'); };
  if (view === 'dungeon') return lazyMount(root, () => import('./engine/dungeon-preview-bootstrap').then((m) => m.mountDungeonPreview(root, user, onLogout)));
  if (view === 'dungeon_instance') return lazyMount(root, () => loadDungeon().then((m) => m.mountDungeonInstance(root, user, onLogout)));
  if (import.meta.env.DEV && view === 'lookdev') {
    // DEV-only art-direction preview (engine/lookdev) — lazy, so it never reaches a prod bundle.
    let dispose: (() => void) | null = null;
    let gone = false;
    void import('./engine/lookdev/lookdev-scene').then((m) => { if (!gone) dispose = m.mountLookdev(root); });
    return () => { gone = true; dispose?.(); };
  }
  return lazyMount(root, () => loadSession().then((m) => m.mountSession(root, user, onLogout)));
}

/** Auth screens get a top-left link back to the landing page and a captcha that can't go stale. */
function withBackLink(screenTeardown: () => void): () => void {
  const unmountLink = mountBackToLanding(document.body);
  const stopFreshness = keepAltchaFresh(app);
  return () => { stopFreshness(); unmountLink(); screenTeardown(); };
}

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

function render() {
  teardown?.();
  teardown = null;

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = withBackLink(renderLogin(app, afterAuth));
      prefetchGame();
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = withBackLink(renderRegister(app, afterAuth));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = withBackLink(renderForgotPassword(app));
      break;
    case '/reset-password':
      teardown = renderResetPassword(app, params.get('token') ?? '', () => navigate('/login'));
      break;
    case '/verify-email':
      teardown = renderVerifyEmail(app, params.get('token') ?? '');
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      break;
    case '/admin-panel':
      if (!user) { navigate('/login'); return; }
      // KYC_ADMIN gets in too; the panel shows that role only the KYC queue,
      // the one admin area core's RolesGuard lets it call.
      if (user.role !== 'ADMIN' && user.role !== 'KYC_ADMIN') { navigate('/play'); return; }
      teardown = lazyMount(app, () => import('./engine/admin-panel/admin-panel-bootstrap').then((m) => m.mountAdminPanel(app, user, () => navigate('/play'))));
      break;
    case '/play':
    case '/':
      if (!user) { navigate('/login'); return; }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      connectSocket();
      teardown = mountByView(params.get('view'), app, user);
      break;
    default:
      navigate('/login');
  }
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

// A reload mid-run is about to reclaim its seat (engine/net/run-resume.ts):
// say so from the first frame instead of the shell's generic "Loading…",
// which was followed by "Descending / Finding the stair down…" — the copy of
// a new run, not of getting back into this one.
const bootVeil = location.pathname === '/play' && peekResumableRun()
  ? new EntryVeil(document.body, 'Reconnecting to your run\u2026', 'Holding your seat\u2026', { accent: 'cyan' })
  : null;

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) {
    stripBrandMeta();
    document.documentElement.dataset.preview = 'true';
  }

  await bootstrapSession();
  render();
  bootVeil?.dispose();

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

/**
 * The page is going away. `pagehide` as well as `beforeunload`: mobile Safari
 * and a phone discarding a backgrounded tab often skip `beforeunload`, and
 * `pagehide` is the one event they reliably fire. Guarded so the two never
 * tear down twice.
 */
let unloadHandled = false;
function onPageGoingAway(): void {
  if (unloadHandled) return;
  unloadHandled = true;
  // Before the teardown: a reload must keep a run's seat, not forfeit it.
  dungeonModule?.markPageUnloading();
  teardown?.();
  dungeonModule?.unmountDungeonInstance();
}
window.addEventListener('beforeunload', onPageGoingAway);
window.addEventListener('pagehide', onPageGoingAway);
// Restored from the back/forward cache after the teardown above: the app is
// gone, so boot it again (which reclaims the seat if it is still held).
window.addEventListener('pageshow', (e) => { if (e.persisted && unloadHandled) location.reload(); });
